import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api'

/**
 * Fetches the backend-declared output_kind for an agent from
 * GET /api/v1/agents/centralized/{name}/docs. Cached per-agent in module scope
 * so the dispatcher never double-fetches during a session.
 */
const CACHE = new Map<string, string>()
const INFLIGHT = new Map<string, Promise<string>>()

async function fetchKind(agentName: string): Promise<string> {
  const cached = CACHE.get(agentName)
  if (cached) return cached
  const existing = INFLIGHT.get(agentName)
  if (existing) return existing
  const p = (async () => {
    try {
      const resp = await (apiClient as any).client.get(
        `/api/v1/agents/centralized/${agentName}/docs`,
      )
      const kind = (resp?.data?.output_kind as string) || 'json'
      CACHE.set(agentName, kind)
      return kind
    } catch {
      CACHE.set(agentName, 'json')
      return 'json'
    } finally {
      INFLIGHT.delete(agentName)
    }
  })()
  INFLIGHT.set(agentName, p)
  return p
}

export function useAgentOutputKind(agentName: string | null | undefined): string | null {
  const [kind, setKind] = useState<string | null>(() =>
    agentName ? CACHE.get(agentName) || null : null,
  )
  useEffect(() => {
    if (!agentName) {
      setKind(null)
      return
    }
    let cancelled = false
    fetchKind(agentName).then((k) => {
      if (!cancelled) setKind(k)
    })
    return () => {
      cancelled = true
    }
  }, [agentName])
  return kind
}
